import React, { useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { isHttpUrl } from "@/lib/utils";
import { useI18n } from "@/contexts/I18nContext";

const formatDate = (iso, lang) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" :
    d.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short", year: "numeric" });
};

/**
 * Retailer prices, cheapest first. Each row opens the affiliate link (after
 * logging the click) in a new tab; the panel is plainly labelled as buying
 * links with the commission disclosure, and nothing else (ads included) is
 * placed inside it.
 */
const COLLAPSED = 3;

export const PriceComparisonPanel = ({ bookId, prices = [] }) => {
  const { t, lang } = useI18n();
  const [expanded, setExpanded] = useState(false);
  if (!prices.length) return null;
  const sorted = [...prices].sort((a, b) => a.price - b.price);
  const updated = sorted.map((p) => p.last_updated).filter(Boolean).sort().pop();
  // Kept from the original panel: how much the cheapest offer saves against Amazon.
  const amazon = sorted.find((p) => p.retailer_name.toLowerCase().includes("amazon"));
  const saving = amazon ? Math.round(amazon.price - sorted[0].price) : 0;

  const open = async (p) => {
    try { await api.post("/affiliate/click", { book_id: bookId, retailer_name: p.retailer_name, price: p.price }); } catch {}
    if (isHttpUrl(p.affiliate_url)) window.open(p.affiliate_url, "_blank", "noopener,sponsored");
  };

  return (
    <section aria-labelledby="where-to-buy" className="border border-border bg-card rounded-[var(--radius)]" data-testid="price-comparison-panel">
      <h2 id="where-to-buy" className="px-4 pt-3 pb-2 text-xl">{t("where_to_buy")}</h2>
      <ul className="divide-y divide-border border-t border-border">
        {sorted.map((p, idx) => (
          // Phones show the three cheapest; the rest are one tap away. Desktop shows all.
          <li key={p.id} className={idx >= COLLAPSED && !expanded ? "hidden lg:block" : undefined}>
            <button
              onClick={() => open(p)} rel="sponsored noopener"
              data-testid={`buy-btn-${p.retailer_name.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-secondary/50 focus-visible:bg-secondary/50"
            >
              {p.retailer_logo && (
                <img src={p.retailer_logo} alt="" width="32" height="32" loading="lazy"
                  className="h-8 w-8 shrink-0 rounded-sm border border-border bg-white object-contain" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block leading-snug break-words line-clamp-2">{p.retailer_name}</span>
                {idx === 0 && sorted.length > 1 && (
                  <span data-testid="best-price-badge" className="text-sm text-success">
                    {t("best_price")}{saving > 0 && ` · ${t("save_vs")} ₹${saving} vs Amazon`}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-lg font-semibold tabular-nums">₹{Math.round(p.price)}</span>
              <ExternalLink size={15} className="shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      {sorted.length > COLLAPSED && !expanded && (
        <button onClick={() => setExpanded(true)} className="w-full border-t border-border px-4 py-2.5 text-left text-primary lg:hidden">
          {t("all_sellers").replace("{n}", sorted.length)}
        </button>
      )}
      <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
        {updated && <>{t("prices_updated")} {formatDate(updated, lang)}. </>}{t("affiliate_note")}
      </p>
    </section>
  );
};
